import { CgSpinnerTwo } from '@react-icons/all-files/cg/CgSpinnerTwo'
import { IoIosCheckmarkCircle } from '@react-icons/all-files/io/IoIosCheckmarkCircle'
import { IoIosCloseCircle } from '@react-icons/all-files/io/IoIosCloseCircle'
import { IoStopCircle } from '@react-icons/all-files/io5/IoStopCircle'
import { MdPending } from '@react-icons/all-files/md/MdPending'
import { IconEye } from '~/components/icons'
import { AgentTaskStatus, CodePlan, Diff } from '~/protos/agents'
import { cm } from '~/utils/utils'
import { CheckIconTwoTone, ErroredSidebarIcon, ProgressCircleIcon } from './icon'

interface TaskStatusIconProps {
  color?: string
  className?: string
  sidebar?: boolean
}

export enum TaskStatusDetailType {
  NONE = 'NONE',
  DOC = 'DOC',
  DIFF = 'DIFF',
  PLAN = 'PLAN',
  INPUT = 'INPUT'
}
export interface TaskStatusDetail {
  text?: string
  diffs?: Diff[]
  plan?: CodePlan
  diffComments?: string[]
}

export interface TypeTaskStatus {
  id: AgentTaskStatus
  group: 'IN_PROGRESS' | 'COMPLETED' | 'NEED_ACTION' | 'ERRORED' | 'STOPPED'
  color: string
  icon: (props: TaskStatusIconProps) => JSX.Element
  detailType: TaskStatusDetailType
}
export type TypeTaskStatuses = {
  [index in AgentTaskStatus]: TypeTaskStatus
}
export type TypeTaskStatusEnum = Exclude<AgentTaskStatus, AgentTaskStatus.UNRECOGNIZED>

const StatusColor = {
  InProgress: getComputedStyle(document.documentElement)
    .getPropertyValue('--color-in-progress')
    .trim(),
  Errored: getComputedStyle(document.documentElement).getPropertyValue('--color-errored').trim(),
  Completed: getComputedStyle(document.documentElement)
    .getPropertyValue('--color-completed')
    .trim(),
  NeedAction: getComputedStyle(document.documentElement)
    .getPropertyValue('--color-need-action')
    .trim()
}

const taskStatuses: { [index in TypeTaskStatusEnum]: Partial<TypeTaskStatus> } = {
  [AgentTaskStatus.NONE_TASK_STATUS]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  },
  [AgentTaskStatus.SUBMITTED]: { group: 'IN_PROGRESS', detailType: TaskStatusDetailType.DOC },
  [AgentTaskStatus.IN_PROGRESS]: { group: 'IN_PROGRESS', detailType: TaskStatusDetailType.NONE },
  [AgentTaskStatus.HUMAN_DIFF_UPDATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DIFF
  },
  [AgentTaskStatus.DATAWEAVE_DATA_CREATION_ASST_DATA_TYPE_REQUESTED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  },
  [AgentTaskStatus.PLANNING_UPDATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.PLAN
  },
  [AgentTaskStatus.DOCUMENT_UPDATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DOC
  },
  [AgentTaskStatus.STEPS_GENERATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DOC
  },
  [AgentTaskStatus.STEPS_UPDATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DOC
  },
  [AgentTaskStatus.DIFF_COMMENT_ADDED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DIFF
  },
  [AgentTaskStatus.PLANNING_REREQUESTED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  },
  [AgentTaskStatus.AGENT_INTERNAL_STATUS]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  },
  [AgentTaskStatus.MUNIT_SCENARIO_GENERATED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.DOC
  },
  [AgentTaskStatus.PLANNING_COMPLETED]: {
    group: 'NEED_ACTION',
    detailType: TaskStatusDetailType.PLAN
  },
  [AgentTaskStatus.DIFF_GENERATED]: { group: 'NEED_ACTION', detailType: TaskStatusDetailType.DIFF },
  [AgentTaskStatus.ERRORED]: { group: 'ERRORED', detailType: TaskStatusDetailType.NONE },
  [AgentTaskStatus.STOPPED]: { group: 'STOPPED', detailType: TaskStatusDetailType.NONE },
  [AgentTaskStatus.DOCUMENT_GENERATED]: {
    group: 'COMPLETED',
    detailType: TaskStatusDetailType.DOC
  },
  [AgentTaskStatus.COMPLETED]: { group: 'COMPLETED', detailType: TaskStatusDetailType.NONE },
  [AgentTaskStatus.ARCHIVED]: { group: 'COMPLETED', detailType: TaskStatusDetailType.NONE },
  [AgentTaskStatus.IMAGE_GENERATED]: { group: 'COMPLETED', detailType: TaskStatusDetailType.NONE },
  [AgentTaskStatus.GENERAL_TXT_FEEDBACK_PROVIDED]: {
    group: 'IN_PROGRESS',
    detailType: TaskStatusDetailType.NONE
  }
} as const
function buildTaskStatuses() {
  return Object.entries(taskStatuses).reduce((acc: TypeTaskStatuses, [key, value]) => {
    const id = Number(key) as TypeTaskStatus['id']
    const status = { ...value, id: Number(key) as TypeTaskStatus['id'] }
    if (status.icon == null) {
      switch (status.group) {
        case 'COMPLETED':
          status.icon = ({ sidebar, className, color }: TaskStatusIconProps) => {
            return sidebar ? (
              <CheckIconTwoTone className={className} />
            ) : (
              <IoIosCheckmarkCircle
                className={className}
                size={22}
                color={color || StatusColor.Completed}
              />
            )
          }
          break
        case 'ERRORED':
          status.icon = ({ sidebar, className, color }: TaskStatusIconProps) => {
            return sidebar ? (
              <ErroredSidebarIcon className={className} />
            ) : (
              <IoIosCloseCircle
                className={className}
                size={22}
                color={color || StatusColor.Errored}
              />
            )
          }
          break
        case 'NEED_ACTION':
          status.icon = ({ sidebar, className, color }: TaskStatusIconProps) => {
            return sidebar ? <IconEye /> : <MdPending size={22} color={color} />
          }
          break
        case 'STOPPED':
          status.icon = (props: TaskStatusIconProps) => (
            <IoStopCircle
              className={props.className}
              size={22}
              color={props.color || StatusColor.Errored}
            />
          )
          break
        default:
          status.icon = ({ sidebar, className, color }: TaskStatusIconProps) => {
            return sidebar ? (
              <ProgressCircleIcon className={cm('animate-spin', className)} />
            ) : (
              <CgSpinnerTwo
                className={cm('animate-spin', className)}
                size={22}
                color={color || StatusColor.InProgress}
              />
            )
          }
          break
      }
    }
    if (status.color == null) {
      switch (status.group) {
        case 'ERRORED':
        case 'STOPPED':
          status.color = StatusColor.Errored
          break
        case 'COMPLETED':
          status.color = StatusColor.Completed
          break
        default:
          status.color = StatusColor.InProgress
      }
    }
    acc[id] = status as TypeTaskStatus
    return acc
  }, {} as TypeTaskStatuses)
}

export const TaskStatuses = buildTaskStatuses()

export const DocumentGeneratedStatuses = [
  AgentTaskStatus.DOCUMENT_GENERATED,
  AgentTaskStatus.MUNIT_SCENARIO_GENERATED
]
export const TerminatedTaskStatus = [
  AgentTaskStatus.COMPLETED,
  AgentTaskStatus.ERRORED,
  AgentTaskStatus.DOCUMENT_GENERATED,
  AgentTaskStatus.STOPPED
]

export function isAwaitingUserInput(status: AgentTaskStatus) {
  return AwaitingUserInputStatuses.includes(status)
}

export function isTerminated(status: AgentTaskStatus, isLocalTask?: boolean) {
  if (isLocalTask && status === AgentTaskStatus.DIFF_GENERATED) {
    return true
  }
  return TerminatedTaskStatus.includes(status)
}

export function isSuccessTerminated(status: AgentTaskStatus) {
  return [
    AgentTaskStatus.COMPLETED,
    AgentTaskStatus.DOCUMENT_GENERATED,
    AgentTaskStatus.IMAGE_GENERATED
  ].includes(status)
}

export function isInProgress(status: AgentTaskStatus) {
  return (
    !isTerminated(status) &&
    !isAwaitingUserInput(status) &&
    !FullScreenTaskStatuses.includes(status)
  )
}

export const FullScreenTaskStatuses = [AgentTaskStatus.DIFF_GENERATED]
export const AwaitingUserInputStatuses = [AgentTaskStatus.NONE_TASK_STATUS]

export const isAgentInternalStatus = (status: AgentTaskStatus) => {
  return status === AgentTaskStatus.AGENT_INTERNAL_STATUS
}
